import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  EXTERNAL_API_AUTH_TYPES,
  EXTERNAL_API_PARAM_LOCATIONS,
  EXTERNAL_API_PARAM_TYPES,
  EXTERNAL_API_RESPONSE_TYPES,
  EXTERNAL_HTTP_METHODS,
  externalApiAuthConfigSchema,
  externalApiParamSchema,
  externalApiResponseFieldSchema,
  externalApiRetryConfigSchema,
} from "@erp/workflows";

/**
 * Public-safe DTO for an `ExternalApiDefinition` row. Auth credentials are
 * never returned in plaintext; the `authConfig` field on responses is a
 * redacted shape so the frontend can render the right form controls
 * without ever seeing the secret.
 */
export const ExternalApiKindSchema = z.enum(["general", "erp_submission"]);

const ExternalApiDefinitionDTOSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  /** 'general' or 'erp_submission' (targetable by the sales-order Submission step). */
  kind: ExternalApiKindSchema,
  isActive: z.boolean(),
  method: z.enum(EXTERNAL_HTTP_METHODS),
  baseUrl: z.string(),
  pathTemplate: z.string(),
  paramSchema: z.array(externalApiParamSchema),
  responseSchema: z.array(externalApiResponseFieldSchema),
  responseBodySchemaDefinitionId: z.string().uuid().nullable(),
  responseBodySchemaDefinitionVersionId: z.string().uuid().nullable(),
  requestBodySchemaDefinitionId: z.string().uuid().nullable(),
  requestBodySchemaDefinitionVersionId: z.string().uuid().nullable(),
  authType: z.enum(EXTERNAL_API_AUTH_TYPES),
  /** Redacted shape — never the real credential material. */
  authConfig: externalApiAuthConfigSchema,
  timeoutMs: z.number().int(),
  retryConfig: externalApiRetryConfigSchema,
  version: z.number().int(),
  lastTestedAt: z.string().nullable(),
  lastTestStatus: z.enum(["success", "failure"]).nullable(),
  lastTestError: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.object({ id: z.string(), name: z.string() }).nullable(),
  updatedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
});

export type ExternalApiDefinitionDTO = z.infer<
  typeof ExternalApiDefinitionDTOSchema
>;

const ListExternalApiDefinitionsRequestSchema = z.object({
  includeInactive: z.boolean().optional(),
  search: z.string().optional(),
  /** Restrict to one kind (e.g. 'erp_submission' for the Submission step). */
  kind: ExternalApiKindSchema.optional(),
});

const ListExternalApiDefinitionsResponseSchema = z.object({
  apiDefinitions: z.array(ExternalApiDefinitionDTOSchema),
});

const GetExternalApiDefinitionRequestSchema = z.object({
  id: z.uuid(),
});

/**
 * Create / update input. `authConfig` may include real credentials (caller
 * is sending them); the handler encrypts before persisting and never echoes
 * them back.
 */
const CreateExternalApiDefinitionInputSchema = z
  .object({
    name: z.string().min(1).max(255),
    kind: ExternalApiKindSchema.optional(),
    description: z.string().max(2000).optional(),
    isActive: z.boolean().optional(),
    method: z.enum(EXTERNAL_HTTP_METHODS),
    baseUrl: z.string().url(),
    pathTemplate: z.string().max(2048).optional(),
    paramSchema: z.array(externalApiParamSchema).max(64),
    responseSchema: z.array(externalApiResponseFieldSchema).max(64),
    responseBodySchemaDefinitionId: z.string().uuid().optional(),
    responseBodySchemaDefinitionVersionId: z.string().uuid().optional(),
    requestBodySchemaDefinitionId: z.string().uuid().optional(),
    requestBodySchemaDefinitionVersionId: z.string().uuid().optional(),
    authType: z.enum(EXTERNAL_API_AUTH_TYPES),
    authConfig: externalApiAuthConfigSchema,
    timeoutMs: z.number().int().min(100).max(600_000).optional(),
    retryConfig: externalApiRetryConfigSchema.optional(),
  })
  .superRefine((val, ctx) => {
    const hasRespD = val.responseBodySchemaDefinitionId !== undefined;
    const hasRespV = val.responseBodySchemaDefinitionVersionId !== undefined;
    if (hasRespD !== hasRespV) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Set both responseBodySchemaDefinitionId and responseBodySchemaDefinitionVersionId, or omit both.",
        path: hasRespD
          ? ["responseBodySchemaDefinitionVersionId"]
          : ["responseBodySchemaDefinitionId"],
      });
    }

    const hasReqD = val.requestBodySchemaDefinitionId !== undefined;
    const hasReqV = val.requestBodySchemaDefinitionVersionId !== undefined;
    if (hasReqD !== hasReqV) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Set both requestBodySchemaDefinitionId and requestBodySchemaDefinitionVersionId, or omit both.",
        path: hasReqD
          ? ["requestBodySchemaDefinitionVersionId"]
          : ["requestBodySchemaDefinitionId"],
      });
    }

    const hasRespPins =
      typeof val.responseBodySchemaDefinitionId === "string" &&
      val.responseBodySchemaDefinitionId.length > 0 &&
      typeof val.responseBodySchemaDefinitionVersionId === "string" &&
      val.responseBodySchemaDefinitionVersionId.length > 0;
    const hasRespManual = val.responseSchema.length >= 1;
    if (!hasRespPins && !hasRespManual) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Describe the API response: pin a response JSON schema, or add at least one manual response field mapping.",
        path: ["responseSchema"],
      });
    }

    const hasReqPins =
      typeof val.requestBodySchemaDefinitionId === "string" &&
      val.requestBodySchemaDefinitionId.length > 0 &&
      typeof val.requestBodySchemaDefinitionVersionId === "string" &&
      val.requestBodySchemaDefinitionVersionId.length > 0;
    const bodyParamCount = val.paramSchema.filter(
      (p) => p.in === "body",
    ).length;

    if (hasReqPins && bodyParamCount > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Remove body parameters from paramSchema when using a request JSON schema pin (the schema drives the JSON body).",
        path: ["paramSchema"],
      });
    }

    if (val.method === "GET") {
      if (hasReqPins) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "GET APIs cannot pin a request JSON body schema.",
          path: ["requestBodySchemaDefinitionId"],
        });
      }
      if (bodyParamCount > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "GET APIs cannot declare body parameters.",
          path: ["paramSchema"],
        });
      }
    }

    if (val.method === "DELETE") {
      if (hasReqPins && bodyParamCount > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Use either a request JSON schema pin or body parameters on paramSchema, not both.",
          path: ["paramSchema"],
        });
      }
    }
  });

const UpdateExternalApiDefinitionInputSchema =
  CreateExternalApiDefinitionInputSchema.safeExtend({
    id: z.uuid(),
    /**
     * When true, the caller intentionally left `authConfig` blank to
     * preserve the existing encrypted credentials (same UX as Fusion's
     * password-omit pattern). The server keeps the existing blob.
     */
    preserveExistingAuth: z.boolean().optional(),
  });

const DeleteExternalApiDefinitionInputSchema = z.object({
  id: z.uuid(),
});

const ToggleActiveInputSchema = z.object({
  id: z.uuid(),
  isActive: z.boolean(),
});

/**
 * `testRequest` accepts either a saved definition id (so the test runs
 * the persisted/encrypted creds) OR an inline definition (so the user can
 * try a config before saving).
 */
const TestRequestInputSchema = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("registered"),
    apiDefinitionId: z.uuid(),
    paramValues: z.record(z.string(), z.unknown()).prefault({}),
  }),
  z.object({
    source: z.literal("inline"),
    definition: CreateExternalApiDefinitionInputSchema,
    paramValues: z.record(z.string(), z.unknown()).prefault({}),
  }),
]);

const TestRequestResponseSchema = z.object({
  ok: z.boolean(),
  status: z.number().int(),
  /** Sanitized response headers as a flat record. */
  responseHeaders: z.record(z.string(), z.string()),
  /** Parsed response body — JSON object or string. */
  responseBody: z.unknown(),
  /** Raw body string (truncated when large). */
  rawBody: z.string(),
  /** Output sockets: manual JSONPath mappings and/or schema-typed root fields. */
  parsedOutputs: z.record(z.string(), z.unknown()),
  /** Final URL after templating; safe to display. */
  finalUrl: z.string(),
  /** Headers actually sent, with credentials redacted. */
  redactedRequestHeaders: z.record(z.string(), z.string()),
  attempts: z.number().int(),
  durationMs: z.number(),
  error: z.string().optional(),
});

export type TestExternalApiRequest = z.infer<typeof TestRequestInputSchema>;
export type TestExternalApiResponse = z.infer<typeof TestRequestResponseSchema>;
export type CreateExternalApiDefinitionInput = z.infer<
  typeof CreateExternalApiDefinitionInputSchema
>;
export type UpdateExternalApiDefinitionInput = z.infer<
  typeof UpdateExternalApiDefinitionInputSchema
>;

export const externalApisContracts = {
  list: oc
    .input(ListExternalApiDefinitionsRequestSchema)
    .output(ListExternalApiDefinitionsResponseSchema)
    .route({
      summary: "List external API definitions",
      description:
        "Lists the organization's configured outbound HTTP API definitions used by workflows, filterable by search text, kind (general or ERP submission) and inactive inclusion. Auth secrets are redacted.",
      method: "GET",
    }),

  get: oc
    .input(GetExternalApiDefinitionRequestSchema)
    .output(ExternalApiDefinitionDTOSchema)
    .route({
      summary: "Get an external API definition",
      description:
        "Returns one outbound HTTP API definition: method, URL template, parameters, response mappings, schema pins, retry settings and last test result, with auth secrets redacted.",
      method: "GET",
    }),

  create: oc
    .input(CreateExternalApiDefinitionInputSchema)
    .output(ExternalApiDefinitionDTOSchema)
    .route({
      summary: "Create an external API definition",
      description:
        "Saves a new outbound HTTP API definition for workflows, with URL template, parameters, response mappings, auth and retry settings. Credentials are encrypted and never echoed back.",
      method: "POST",
    }),

  update: oc
    .input(UpdateExternalApiDefinitionInputSchema)
    .output(ExternalApiDefinitionDTOSchema)
    .route({
      summary: "Update an external API definition",
      description:
        "Edits an outbound HTTP API definition's request, response and auth settings; leaving auth blank with preserve-existing keeps stored encrypted secrets. Returns the redacted definition.",
      method: "POST",
    }),

  delete: oc
    .input(DeleteExternalApiDefinitionInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Delete an external API definition",
      description:
        "Permanently removes an outbound HTTP API definition from the organization.",
      method: "POST",
    }),

  toggleActive: oc
    .input(ToggleActiveInputSchema)
    .output(ExternalApiDefinitionDTOSchema)
    .route({
      summary: "Activate or deactivate an external API",
      description:
        "Turns an outbound HTTP API definition on or off for use by workflows. Returns the updated definition.",
      method: "POST",
    }),

  testRequest: oc
    .input(TestRequestInputSchema)
    .output(TestRequestResponseSchema)
    .route({
      summary: "Test an external API request",
      description:
        "Makes a live HTTP call using a saved definition or an unsaved inline one, with sample parameter values. Returns status, response, parsed outputs and redacted headers; records saved-definition test results.",
      method: "POST",
    }),
} as const;

export type ExternalApisContracts = typeof externalApisContracts;

// Re-export the value-types so consumers don't need to import from
// `@erp/workflows` to discriminate on `authType`/`paramType`/etc.
export {
  EXTERNAL_API_AUTH_TYPES,
  EXTERNAL_API_PARAM_LOCATIONS,
  EXTERNAL_API_PARAM_TYPES,
  EXTERNAL_API_RESPONSE_TYPES,
  EXTERNAL_HTTP_METHODS,
};
